/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { SalesforceCaseDescriptionSource } from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import type {
  HandoffOption,
  HandoffOptionGroup,
} from 'embercom/components/workflows/graph-editor/node-items/steps/handoff-to-salesforce-case';
import { BlocksDocument } from '@intercom/composer-ember';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { StandaloneComposerConfig } from 'embercom/objects/standalone/composer-config';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import type IntercomAttribute from 'embercom/models/attribute';
import type { Block } from 'embercom/models/common/blocks/block';
import type RouterService from '@ember/routing/router-service';
import { type SafeString } from '@ember/template/-private/handlebars';

interface Arguments {
  handoverOptions: HandoffOptionGroup[];
  selectedHandoverOption: HandoffOption | undefined;
  isReadOnly: boolean;
  isLoading: boolean;
  className?: string;
  setHandoffToSalesforceOwnerId: (handoffId: string) => void;
  isHandoffToSalesforceCase: boolean;
  bannerHeading: string;
  caseDescriptionSource?: SalesforceCaseDescriptionSource;
  setCaseDescriptionSource: (source: SalesforceCaseDescriptionSource) => void;
  caseSubjectBlocks?: BlocksDocument;
  onCaseSubjectChange?: (value: BlocksDocument) => void;
  caseDescriptionBlocks?: BlocksDocument;
  onCaseDescriptionChange?: (value: BlocksDocument) => void;
}

const EXCLUDED_ATTRIBUTES = [
  'fin_preview',
  'ai_tone_of_voice',
  'ai_answer_length',
  'ai_language_style',
  'ai_chatbot_resolution_state',
  'fin_actions_used',
  'standalone_zendesk_brand',
  'ai_insights_topic',
  'ai_insights_subtopic',
  'ai_insights_topic_subtopic',
  'ai_insights_conversation_summary',
];

export default class Settings extends Component<Arguments> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare attributeService: $TSFixMe;
  @service declare router: RouterService;

  @tracked caseSubjectBlocksDoc: BlocksDocument;
  @tracked caseDescriptionBlocksDoc: BlocksDocument;
  caseSubjectComposerConfig: StandaloneComposerConfig;
  caseDescriptionComposerConfig: StandaloneComposerConfig;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
    this.caseSubjectBlocksDoc = args.caseSubjectBlocks || new BlocksDocument([]);
    this.caseDescriptionBlocksDoc = args.caseDescriptionBlocks || new BlocksDocument([]);

    let attributeResolver = new AttributeInfoResolver({
      attributes: this.appService.app.allowedAttributes,
      attributeAllowlist: this.attributeService.attributesToGroupList(this.allowedAttributes),
      conversationAttributes:
        this.attributeService.nonArchivedConversationCustomAttributesWithoutFile,
      includeAppAttributes: false,
    });

    this.caseSubjectComposerConfig = new StandaloneComposerConfig({
      resolver: attributeResolver,
      autoFocus: false,
    });

    this.caseDescriptionComposerConfig = new StandaloneComposerConfig({
      resolver: attributeResolver,
      singleBlockMode: false,
      autoFocus: false,
    });
  }

  get app() {
    return this.appService.app;
  }

  get allowedAttributes() {
    return this.attributeService.userEditorMetadataAndConversationAttributes.reject(
      (attr: IntercomAttribute) =>
        attr.isUserEvent || attr.isCompany || EXCLUDED_ATTRIBUTES.includes(attr.cdaId || ''),
    );
  }

  get hasSubjectContent() {
    return this.caseSubjectBlocksDoc.blocks.some(
      (block: Block) => block.text && block.text.trim().length > 0,
    );
  }

  get hasDescriptionContent() {
    return this.caseDescriptionBlocksDoc.blocks.some(
      (block: Block) => block.text && block.text.trim().length > 0,
    );
  }

  get tweakedHandoverOptions(): HandoffOptionGroup[] {
    return this.args.handoverOptions.map((group) => ({
      ...group,
      items: group.items.map((item) => ({
        ...item,
        componentClasses: 'twk__standalone-dropdown-item',
      })),
    }));
  }

  get salesforceConfig() {
    return this.finStandaloneService.salesforcePlatform.salesforceConfig!;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get caseDescriptionSource(): SalesforceCaseDescriptionSource {
    return this.args.caseDescriptionSource || SalesforceCaseDescriptionSource.TEXT;
  }

  get defaultLabelForCaseOwner(): string {
    return this.args.isHandoffToSalesforceCase
      ? this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.unassigned-owner-label',
        )
      : this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.select-agent-or-queue-default-label',
        );
  }

  get handoffToTeamCaseDataSteps(): { text: SafeString }[] {
    let steps = [
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.case-data-for-handoff-to-team.step-1',
          {
            htmlSafe: true,
          },
        ),
      },
    ];

    if (this.app.canUseFinFirstAiCategories) {
      steps.push({
        text: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.case-data-for-handoff-to-team.step-2-fin-first-ai-categories',
          {
            aiCategoriesUrl: this.router.urlFor('apps.app.standalone.categories'),
            htmlSafe: true,
          },
        ),
      });
    } else {
      steps.push({
        text: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.case-data-for-handoff-to-team.step-2-category-detection',
          {
            htmlSafe: true,
          },
        ),
      });
    }

    return steps as { text: SafeString }[];
  }

  @action
  onCaseSubjectChange(document: BlocksDocument) {
    this.caseSubjectBlocksDoc = document;
    this.args.onCaseSubjectChange?.(document);
  }

  @action
  onCaseDescriptionChange(document: BlocksDocument) {
    this.caseDescriptionBlocksDoc = document;
    this.args.onCaseDescriptionChange?.(document);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::HandoffToSalesforceCase::Settings': typeof Settings;
    'workflows/graph-editor/node-items/steps/handoff-to-salesforce-case/settings': typeof Settings;
  }
}
